// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

import {
    FRANCE_BATCH_ENABLED_COMPANY_CODES,
    FRANCE_LEGAL_ENTITY_BY_CODE,
    FRANCE_TERRITORY_CODE_SET,
    REQUIRED_CURRENCY,
    SIREN_PATTERN,
    SIRET_PATTERN,
    VALID_FRENCH_VAT_RATE_SET,
    buyerResolvesToFrenchEntity,
} from './franceConstants';
import { ManualInvoiceFormState, ValidationError } from './types';
import { isNumericAmount, parseAmount, round2 } from './payloadBuilder';

// Mirrors RequestGateway (RequestInfoBasicCheck) + EdicomFranceValidator client-side
// so the user gets the same failures before ever calling the service.
export function validateForm(state: ManualInvoiceFormState): ValidationError[] {
    const errors: ValidationError[] = [];
    const add = (field: string, message: string): void => {
        errors.push({ field, message });
    };

    const isTd17 = state.invoiceTypeCode === 'TD17';

    // --- RequestGateway: RequestInfoBasicCheck ---
    if (!state.documentId || state.documentId.trim() === '') {
        add('documentId', 'Document ID is required.');
    }
    if (!state.seller.companyName || state.seller.companyName.trim() === '') {
        add('seller.companyName', 'Seller company name is required.');
    }

    // --- EdicomFranceValidator: header ---
    // The header country code must be FR or a French territory for ALL documents,
    // including TD17. Domestic DOM codes (MQ/GP/RE) are only meaningful for the
    // SoldTo/buyer country on the backend, never the header country — the validator
    // rejects them here (CountryCodes.FranceTerritoryCountryCodes excludes MQ/GP/RE).
    const country = (state.countryCode || '').trim().toUpperCase();
    if (!country || !FRANCE_TERRITORY_CODE_SET.has(country)) {
        add('countryCode', 'Country code must be France or a French territory (e.g. FR, GF, MC).');
    }

    if (!state.documentDate) {
        add('documentDate', 'Document date is required.');
    }

    if (!state.transactionCurrencyCode || state.transactionCurrencyCode.trim().toUpperCase() !== REQUIRED_CURRENCY) {
        add('transactionCurrencyCode', `Transaction currency must be ${REQUIRED_CURRENCY} for France.`);
    }

    const companyCode = (state.companyCode || '').trim();
    if (companyCode === '') {
        add('companyCode', 'Company code is required.');
    } else if (isTd17) {
        // For TD17 the seller is a non-French entity; a France batch-enabled code would
        // bypass the AP/PurchaseReport routing path.
        if (FRANCE_BATCH_ENABLED_COMPANY_CODES.has(companyCode)) {
            add('companyCode', 'For TD17, the seller company code must be a non-French entity (not a France-registered/MIOL code).');
        }
    } else if (!FRANCE_LEGAL_ENTITY_BY_CODE.has(companyCode)) {
        add('companyCode', 'Company code is not a registered France legal entity.');
    }

    // --- TD17-specific rules ---
    if (isTd17) {
        if (state.documentType === 'paymentReceipt') {
            add('documentType', 'TD17 (purchase report) does not support Payment Receipt. Use Invoice or Credit Note.');
        }
        if (!buyerResolvesToFrenchEntity(state.buyer.siren, state.buyer.vatId)) {
            add('buyer.frenchEntity', 'For TD17, the buyer must be a French Microsoft entity — its SIREN or VAT ID must match one of the registered French entities (1073, 1081, 1985, 1780, 1672).');
        }
    }

    // Seller address required.
    const sellerAddr = state.seller.address;
    if (!sellerAddr || (sellerAddr.line1.trim() === '' && sellerAddr.city.trim() === '' && sellerAddr.postalCode.trim() === '')) {
        add('seller.address', 'Seller address is required.');
    }

    // --- SIREN / SIRET format (seller + buyer, only when provided) ---
    if (state.seller.siren.trim() !== '' && !SIREN_PATTERN.test(state.seller.siren.trim())) {
        add('seller.siren', 'Seller SIREN must be exactly 9 digits.');
    }
    if (state.seller.siret.trim() !== '' && !SIRET_PATTERN.test(state.seller.siret.trim())) {
        add('seller.siret', 'Seller SIRET must be exactly 14 digits.');
    }
    if (state.buyer.siren.trim() !== '' && !SIREN_PATTERN.test(state.buyer.siren.trim())) {
        add('buyer.siren', 'Buyer SIREN must be exactly 9 digits.');
    }
    if (state.buyer.siret.trim() !== '' && !SIRET_PATTERN.test(state.buyer.siret.trim())) {
        add('buyer.siret', 'Buyer SIRET must be exactly 14 digits.');
    }

    // --- Customer ---

    const isPaymentReceipt = state.documentType === 'paymentReceipt';

    // --- Line items (required except PaymentReceipt) ---
    if (!isPaymentReceipt) {
        if (state.lineItems.length === 0) {
            add('lineItems', 'At least one line item is required.');
        }
        state.lineItems.forEach((line, i) => {
            if (!line.description || line.description.trim() === '') {
                add(`lineItems[${i}].description`, 'Line item description is required.');
            }
            if (parseAmount(line.quantity) <= 0) {
                add(`lineItems[${i}].quantity`, 'Quantity must be greater than zero.');
            }
            if (line.unitPrice.trim() === '') {
                add(`lineItems[${i}].unitPrice`, 'Unit price is required.');
            } else if (!isNumericAmount(line.unitPrice)) {
                add(`lineItems[${i}].unitPrice`, 'Unit price must be a number.');
            } else if (parseAmount(line.unitPrice) < 0) {
                add(`lineItems[${i}].unitPrice`, 'Unit price cannot be negative.');
            }
            if (line.vatRate === '') {
                add(`lineItems[${i}].vatRate`, 'A VAT rate is required.');
            } else if (!VALID_FRENCH_VAT_RATE_SET.has(line.vatRate)) {
                add(`lineItems[${i}].vatRate`, 'VAT rate is not a valid French rate.');
            }
        });
    } else {
        // PaymentReceipt uses manually entered document amounts.
        const totalAmount = parseAmount(state.manualAmounts.totalAmount);
        if (state.manualAmounts.totalAmount.trim() === '' || totalAmount === 0) {
            add('manualAmounts.totalAmount', 'Total amount is required for a payment receipt.');
        }
    }

    // --- Payment receipt: manual tax amounts must reconcile to a valid VAT rate ---
    if (isPaymentReceipt) {
        const taxAmount = parseAmount(state.manualAmounts.taxAmount);
        const taxableAmount = parseAmount(state.manualAmounts.taxableAmount);
        // A non-zero tax must have a positive taxable base so the payload can emit a
        // reconciling taxDetails entry (buildPayload derives the rate from it), and
        // the derived rate must be a valid French VAT rate — EdicomFranceValidator
        // rejects any TaxDetails.TaxPercentage that is not an allowed rate.
        if (taxAmount !== 0) {
            if (taxableAmount <= 0) {
                add('manualAmounts.taxableAmount', 'Taxable amount must be greater than zero when a tax amount is entered.');
            } else {
                // Use the same rounding helper as buildPayload (payloadBuilder.ts) so the rate the
                // validator checks is identical to the taxPercentage the payload emits — otherwise a
                // value approved here could be serialized differently and rejected by the backend.
                const derivedRate = round2((taxAmount / taxableAmount) * 100);
                if (!VALID_FRENCH_VAT_RATE_SET.has(derivedRate)) {
                    add('manualAmounts.taxAmount', `The tax and taxable amounts imply a VAT rate of ${derivedRate}%, which is not a valid French rate. Adjust the amounts so they reconcile to a valid rate.`);
                }
            }
        }
    }

    // --- CreditNote must reference an original document ---
    if (state.documentType === 'creditNote' && state.reference.documentId.trim() === '') {
        add('reference.documentId', 'A credit note must reference the original document ID.');
    }

    return errors;
}
